import { AlertTriangle, CheckCircle, Info, X, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

type AlertType = 'info' | 'success' | 'warning' | 'error';
type AlertVariant = 'strip' | 'card';

interface AlertDisplayProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>, ForwardedHTMLProps {
  children: React.ReactNode;
  type?: AlertType;
  title?: string;
  icon?: React.ReactNode | false;
  dismissible?: boolean;
  onDismiss?: () => void;
  autoClose?: number;
  size?: ComponentSize;
  bordered?: boolean;
  filled?: boolean;
  variant?: AlertVariant;
  className?: string;
}

const alertVariants: Record<AlertType, { bg: string; filledBg: string; text: string; border: string }> = {
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    filledBg: 'bg-blue-600 dark:bg-blue-700',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-800',
  },
  success: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    filledBg: 'bg-emerald-600 dark:bg-emerald-700',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    filledBg: 'bg-amber-600 dark:bg-amber-700',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-800',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    filledBg: 'bg-red-600 dark:bg-red-700',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-200 dark:border-red-800',
  },
};

const defaultIcons: Record<AlertType, React.ReactNode> = {
  info: <Info className="w-5 h-5" />,
  success: <CheckCircle className="w-5 h-5" />,
  warning: <AlertTriangle className="w-5 h-5" />,
  error: <XCircle className="w-5 h-5" />,
};

const sizePadding: Record<ComponentSize, string> = {
  xs: 'px-2 py-1.5',
  sm: 'px-3 py-2',
  md: 'px-4 py-3',
  lg: 'px-5 py-4',
  xl: 'px-6 py-5',
};

const sizeText: Record<ComponentSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
};

const cardVariants: Record<AlertType, { bg: string; border: string }> = {
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-600',
  },
  success: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    border: 'border-emerald-600',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-600',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-600',
  },
};

const AlertDisplayInner = React.forwardRef<HTMLDivElement, AlertDisplayProps>(
  (
    {
      children,
      type = 'info',
      title,
      icon,
      dismissible = false,
      onDismiss,
      autoClose,
      size = 'md',
      bordered = true,
      filled = false,
      variant = 'strip',
      className,
      ...props
    },
    ref
  ) => {
    const forwardedProps = forwardProps(props);
    const [dismissed, setDismissed] = useState(false);

    const alertStyle = alertVariants[type];
    const cardStyle = cardVariants[type];
    const iconElement = icon === false ? null : icon || defaultIcons[type];
    const isCard = variant === 'card';

    const handleDismiss = useCallback(() => {
      setDismissed(true);
      onDismiss?.();
    }, [onDismiss]);

    useEffect(() => {
      if (autoClose && autoClose > 0) {
        const timer = setTimeout(handleDismiss, autoClose);
        return () => clearTimeout(timer);
      }
    }, [autoClose, handleDismiss]);

    const alertClasses = useMemo(() => {
      if (isCard) {
        return cn('relative rounded-md p-6 border-l-4', cardStyle.bg, cardStyle.border, className);
      }
      return cn(
        'relative rounded-xl',
        sizePadding[size],
        filled ? cn(alertStyle.filledBg, 'text-white') : cn(alertStyle.bg, alertStyle.text),
        bordered && !filled && cn('border', alertStyle.border),
        className
      );
    }, [alertStyle, cardStyle, isCard, size, bordered, filled, className]);

    if (dismissed) return null;

    return (
      <section ref={ref} className={alertClasses} role="alert" aria-live="polite" {...forwardedProps} {...props}>
        <span className="flex items-start gap-3">
          {iconElement && (
            <span className="flex-shrink-0 mt-0.5" aria-hidden="true">
              {iconElement}
            </span>
          )}
          <span className={cn('flex-1 min-w-0', !isCard && sizeText[size])}>
            {title &&
              (isCard ? (
                <span className="block font-serif text-lg font-semibold text-slate-900 dark:text-white mb-1">
                  {title}
                </span>
              ) : (
                <span className="block font-semibold mb-1">{title}</span>
              ))}
            <span className={cn('block', isCard && 'text-sm text-slate-700 dark:text-slate-300 font-sans')}>
              {children}
            </span>
          </span>
          {dismissible && (
            <button
              type="button"
              onClick={handleDismiss}
              className={cn(
                'flex-shrink-0',
                'p-1 rounded-md -m-1',
                filled ? 'hover:bg-white/20' : 'hover:bg-black/5 dark:hover:bg-white/10',
                'transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-current/30'
              )}
              aria-label="Dismiss alert"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </span>
      </section>
    );
  }
);

AlertDisplayInner.displayName = 'AlertDisplay';

export default AlertDisplayInner;
export { AlertDisplayInner as AlertDisplay };
